import { supabase } from './supabase';

// Types
export interface ProjectMember {
    id: string;
    project_id: string;
    user_id: string;
    role: 'viewer' | 'editor';
    invited_by: string;
    status: 'pending' | 'accepted' | 'declined';
    joined_at: string | null;
    created_at: string;
    // Joined profile data
    user?: {
        id: string;
        username: string;
        avatar_url?: string;
    };
    inviter?: {
        id: string;
        username: string;
        avatar_url?: string;
    };
}

export interface ProjectWithOwner {
    id: string;
    user_id: string;
    title: string;
    description?: string;
    project_type: string;
    created_at: string;
    updated_at: string;
    owner?: {
        id: string;
        username: string;
        avatar_url?: string;
    };
    member_role?: 'viewer' | 'editor';
}

export type ProjectRole = 'owner' | 'editor' | 'viewer' | null;

export interface ProjectAccessResult {
    hasAccess: boolean;
    role: ProjectRole;
}

export const ProjectMemberService = {
    /**
     * Get all members of a project (including pending invitations for owner)
     */
    getProjectMembers: async (projectId: string): Promise<ProjectMember[]> => {
        const { data, error } = await supabase
            .from('project_members')
            .select(`
                *,
                user:profiles!project_members_user_id_fkey(id, username, avatar_url),
                inviter:profiles!project_members_invited_by_fkey(id, username, avatar_url)
            `)
            .eq('project_id', projectId)
            .order('created_at', { ascending: true });

        if (error) {
            console.error('ProjectMemberService: Error fetching members', error);
            return [];
        }

        return data as ProjectMember[];
    },

    /**
     * Invite a user to a project by username
     */
    inviteMember: async (
        projectId: string,
        username: string,
        role: 'viewer' | 'editor'
    ): Promise<{ success: boolean; member?: ProjectMember; error?: string }> => {
        // First find the user by username
        const { data: userProfile, error: userError } = await supabase
            .from('profiles')
            .select('id, username')
            .eq('username', username)
            .single();

        if (userError || !userProfile) {
            return { success: false, error: 'User not found' };
        }

        // Check if user is the project owner
        const { data: project } = await supabase
            .from('projects')
            .select('user_id')
            .eq('id', projectId)
            .single();

        if (project?.user_id === userProfile.id) {
            return { success: false, error: 'Cannot invite the project owner' };
        }

        // Check if already a member
        const { data: existingMember } = await supabase
            .from('project_members')
            .select('id, status')
            .eq('project_id', projectId)
            .eq('user_id', userProfile.id)
            .single();

        if (existingMember) {
            if (existingMember.status === 'accepted') {
                return { success: false, error: 'User is already a member' };
            } else if (existingMember.status === 'pending') {
                return { success: false, error: 'User already has a pending invitation' };
            }
            // If declined, we can re-invite (delete old and create new)
            await supabase
                .from('project_members')
                .delete()
                .eq('id', existingMember.id);
        }

        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
            return { success: false, error: 'Not authenticated' };
        }

        // Create the invitation
        const { data: member, error: insertError } = await supabase
            .from('project_members')
            .insert({
                project_id: projectId,
                user_id: userProfile.id,
                role,
                invited_by: user.id,
                status: 'pending'
            })
            .select(`
                *,
                user:profiles!project_members_user_id_fkey(id, username, avatar_url),
                inviter:profiles!project_members_invited_by_fkey(id, username, avatar_url)
            `)
            .single();

        if (insertError) {
            console.error('ProjectMemberService: Error inviting member', insertError);
            return { success: false, error: 'Failed to send invitation' };
        }

        return { success: true, member: member as ProjectMember };
    },

    /**
     * Accept a project invitation
     */
    acceptInvitation: async (membershipId: string): Promise<boolean> => {
        const { error } = await supabase
            .from('project_members')
            .update({ status: 'accepted' })
            .eq('id', membershipId);

        if (error) {
            console.error('ProjectMemberService: Error accepting invitation', error);
            return false;
        }

        return true;
    },

    /**
     * Decline a project invitation
     */
    declineInvitation: async (membershipId: string): Promise<boolean> => {
        const { error } = await supabase
            .from('project_members')
            .update({ status: 'declined' })
            .eq('id', membershipId);

        if (error) {
            console.error('ProjectMemberService: Error declining invitation', error);
            return false;
        }

        return true;
    },

    /**
     * Update a member's role (owner only)
     */
    updateMemberRole: async (
        membershipId: string,
        role: 'viewer' | 'editor'
    ): Promise<boolean> => {
        const { error } = await supabase
            .from('project_members')
            .update({ role })
            .eq('id', membershipId);

        if (error) {
            console.error('ProjectMemberService: Error updating role', error);
            return false;
        }

        return true;
    },

    /**
     * Remove a member from a project (owner only)
     */
    removeMember: async (membershipId: string): Promise<boolean> => {
        const { error } = await supabase
            .from('project_members')
            .delete()
            .eq('id', membershipId);

        if (error) {
            console.error('ProjectMemberService: Error removing member', error);
            return false;
        }

        return true;
    },

    /**
     * Leave a project (member removes themselves)
     */
    leaveProject: async (projectId: string): Promise<boolean> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        const { error } = await supabase
            .from('project_members')
            .delete()
            .eq('project_id', projectId)
            .eq('user_id', user.id);

        if (error) {
            console.error('ProjectMemberService: Error leaving project', error);
            return false;
        }

        return true;
    },

    /**
     * Get all projects the current user has joined (is a member of, not owner)
     */
    getJoinedProjects: async (): Promise<ProjectWithOwner[]> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await supabase
            .from('project_members')
            .select(`
                role,
                project:projects!project_members_project_id_fkey(
                    id,
                    user_id,
                    title,
                    description,
                    project_type,
                    created_at,
                    updated_at,
                    owner:profiles!projects_user_id_fkey(id, username, avatar_url)
                )
            `)
            .eq('user_id', user.id)
            .eq('status', 'accepted');

        if (error) {
            console.error('ProjectMemberService: Error fetching joined projects', error);
            return [];
        }

        // Flatten the structure
        return (data || []).map((item: any) => ({
            ...item.project,
            member_role: item.role
        }));
    },

    /**
     * Check if the current user has access to a project and what role they have
     */
    checkProjectAccess: async (projectId: string): Promise<ProjectAccessResult> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return { hasAccess: false, role: null };

        // Check if owner
        const { data: project } = await supabase
            .from('projects')
            .select('user_id')
            .eq('id', projectId)
            .single();

        if (project?.user_id === user.id) {
            return { hasAccess: true, role: 'owner' };
        }

        // Check if member
        const { data: membership } = await supabase
            .from('project_members')
            .select('role, status')
            .eq('project_id', projectId)
            .eq('user_id', user.id)
            .single();

        if (membership?.status === 'accepted') {
            return { hasAccess: true, role: membership.role as ProjectRole };
        }

        return { hasAccess: false, role: null };
    },

    /**
     * Get a pending invitation by project ID for the current user
     */
    getPendingInvitation: async (projectId: string): Promise<ProjectMember | null> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data, error } = await supabase
            .from('project_members')
            .select(`
                *,
                user:profiles!project_members_user_id_fkey(id, username, avatar_url),
                inviter:profiles!project_members_invited_by_fkey(id, username, avatar_url)
            `)
            .eq('project_id', projectId)
            .eq('user_id', user.id)
            .eq('status', 'pending')
            .single();

        if (error || !data) return null;
        return data as ProjectMember;
    },

    /**
     * Get membership ID by project ID for current user
     */
    getMembershipId: async (projectId: string): Promise<string | null> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data } = await supabase
            .from('project_members')
            .select('id')
            .eq('project_id', projectId)
            .eq('user_id', user.id)
            .single();

        return data?.id || null;
    },

    /**
     * Request to join a project - creates a notification for the project owner
     */
    requestToJoinProject: async (projectId: string): Promise<boolean> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        try {
            // Get project owner
            const { data: project, error: projectError } = await supabase
                .from('projects')
                .select('user_id, title')
                .eq('id', projectId)
                .single();

            if (projectError || !project) {
                console.error('Failed to get project:', projectError);
                return false;
            }

            // Don't allow requesting to join your own project
            if (project.user_id === user.id) {
                console.log('Cannot request to join your own project');
                return false;
            }

            // Check if already a member or has pending request
            const { data: existingMember } = await supabase
                .from('project_members')
                .select('id, status')
                .eq('project_id', projectId)
                .eq('user_id', user.id)
                .single();

            if (existingMember) {
                console.log('Already a member or has pending invitation');
                return false;
            }

            // Check if there's already a pending join request notification
            const { data: existingRequest } = await supabase
                .from('notifications')
                .select('id')
                .eq('type', 'project_join_request')
                .eq('actor_id', user.id)
                .eq('project_id', projectId)
                .eq('user_id', project.user_id)
                .single();

            if (existingRequest) {
                console.log('Already sent a join request');
                return false;
            }

            // Create notification for project owner
            const { error: notificationError } = await supabase
                .from('notifications')
                .insert({
                    type: 'project_join_request',
                    user_id: project.user_id,  // Owner receives the notification
                    actor_id: user.id,         // Requester is the actor
                    project_id: projectId,
                    is_read: false
                });

            if (notificationError) {
                console.error('Failed to create join request notification:', notificationError);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error requesting to join project:', error);
            return false;
        }
    },

    /**
     * Approve a join request - creates project membership and notifies requester
     */
    approveJoinRequest: async (requesterId: string, projectId: string, notificationId: string): Promise<boolean> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        try {
            // Verify current user is the project owner
            const { data: project } = await supabase
                .from('projects')
                .select('user_id')
                .eq('id', projectId)
                .single();

            if (project?.user_id !== user.id) {
                console.error('Only project owner can approve join requests');
                return false;
            }

            // Create membership for requester
            const { error: memberError } = await supabase
                .from('project_members')
                .insert({
                    project_id: projectId,
                    user_id: requesterId,
                    role: 'viewer',  // Default to viewer
                    invited_by: user.id,
                    status: 'accepted',
                    joined_at: new Date().toISOString()
                });

            if (memberError) {
                console.error('Failed to create membership:', memberError);
                return false;
            }

            // Delete the join request notification
            await supabase
                .from('notifications')
                .delete()
                .eq('id', notificationId);

            // Notify the requester that they've been accepted
            await supabase
                .from('notifications')
                .insert({
                    type: 'project_invite',  // Re-use project_invite type to show acceptance
                    user_id: requesterId,
                    actor_id: user.id,
                    project_id: projectId,
                    message: 'Your request to join was approved!',
                    is_read: false
                });

            return true;
        } catch (error) {
            console.error('Error approving join request:', error);
            return false;
        }
    },

    /**
     * Decline a join request - just deletes the notification
     */
    declineJoinRequest: async (notificationId: string): Promise<boolean> => {
        try {
            const { error } = await supabase
                .from('notifications')
                .delete()
                .eq('id', notificationId);

            if (error) {
                console.error('Failed to delete join request notification:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error declining join request:', error);
            return false;
        }
    }
};
